import * as React from 'react';
import { SideNav, Nav as BaseNav, INavTheme, INavSelectionArg } from 'react-sidenav';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { OrganType } from '@nkr/enums/organ-type';
import styled from 'styled-components';



type NavTarget = { isOutside: boolean; url: string };

const keyMap = (organType: OrganType): Record<string, NavTarget> => ({
    'ACTION-CENTER': { isOutside: false, url: '' },
    'DONORS': { isOutside: false, url: 'donors/search' },
    'DONOR-INTAKE': { isOutside: false, url: 'donors/donor-intake/to-do' },
    'RECIPIENTS': { isOutside: false, url: 'recipients' },
    'DASH': { isOutside: true, url: '/centers/donor-intake/index.php?ctrID=' },
    'BILLING': { isOutside: false, url: organType == OrganType.Kidney ? 'billing/accounts-receivable' : 'billing/accounts-payable-total' },
    'REPORTS': { isOutside: false, url: 'reports/dash-board/summary' },
    'SETTINGS': { isOutside: false, url: 'profile/edit' },
    'EXPERT-SYSTEM': { isOutside: false, url: 'expert-system' },
    'MEMBER-HOME': { isOutside: false, url: 'center/' },
    'LEGAL': { isOutside: false, url: 'legal' },
    'REGULATORY-COMPLIANCE': { isOutside: false, url: 'regulatory-compliance' },
    'DSD-RECIPIENTS': { isOutside: false, url: 'recipients/donor-shield-direct' }
});

const IconCnt = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 3px;
`;

const Nav = styled(BaseNav)`
    flex-direction: column;
`;

const theme: INavTheme = {
    color: 'white',
    selectionColor: 'black',
    hoverBgColor: '#a0c5e5',
    selectionBgColor: 'white',
    bgColor: '#337ab7'
};

const nlrTheme: INavTheme = {
    ...theme,
    bgColor: '#3B76B5'
}

const Text = styled.div`
    font-size: 14px;
    text-transform: none;
    text-align: center;

    @media (max-width: 1823px) {
        font-size: 12px;
    }
`;

const NavImg = (props: { dark: string, light: string, selected: boolean }) => (
    <img src={props.selected ? props.dark : props.light} style={{ width: 20, height: 20, objectFit: 'contain' }} />
);

interface IProps {
    centerId: number,
    activeKey: string,
    hasCenterBilling: boolean,
    onTabSelect: (key: string) => void
}

const RenderItems2 = (props: IProps) => {
    const { app } = useAppHookContext();

    const onItemSelection = (arg: INavSelectionArg) => {
        const m = keyMap(app.site.organType)[arg.id.toUpperCase()];
        if (!m) return;

        if (!m.isOutside) {
            props.onTabSelect(m.url);
        } else {
            window.location.href = __OUTSIDEURL__ + m.url + props.centerId;
        }
    };

    const getNavStyle = (key: string[]) => {
        if (key.indexOf(props.activeKey.toUpperCase()) >= 0)
            return { borderColor: '#337ab7', borderWidth: '0px 0px 1px 1px', borderStyle: 'solid', backgroundColor: '#EEE', color: 'black' };
        else {
            return { borderBottom: '1px solid white' };
        }
    }


    const isNkr = app.site.organType == OrganType.Kidney;
    const sideNavTheme = isNkr ? theme : nlrTheme;

    const isActive = (...keys: string[]) => keys.some(k => props.activeKey.toUpperCase() == k);

    return (
        <SideNav
            selectedPath={props.activeKey}
            theme={sideNavTheme}
            onItemSelection={onItemSelection}
        >
            <Nav id='action-center' style={{ ...getNavStyle(['ACTION-CENTER']), borderTopLeftRadius: 4 }}>
                <IconCnt>
                    <NavImg dark='/images/nav/nav-icon-dark-Action-Center.svg' light='/images/nav/nav-icon-light-Action-Center.svg' selected={isActive('ACTION-CENTER')} />
                </IconCnt>
                <Text>Action Center</Text>
            </Nav>

            {isNkr ?
                <Nav id='donors' style={getNavStyle(['DONORS', 'MICROSITES'])}>
                    <IconCnt>
                        <NavImg dark='/images/nav/nav-icon-dark-Donor.svg' light='/images/nav/nav-icon-light-Donor.svg' selected={isActive('DONORS', 'MICROSITES')} />
                    </IconCnt>
                    <Text>Donors</Text>
                </Nav>
                :
                <Nav id='donor-intake' style={getNavStyle(['DONORS', 'MICROSITES'])}>
                    <IconCnt>
                        <NavImg dark='/images/nav/nav-icon-dark-Donor.svg' light='/images/nav/nav-icon-light-Donor.svg' selected={isActive('DONORS', 'MICROSITES')} />
                    </IconCnt>
                    <Text>Donors</Text>
                </Nav>
            }

            {isNkr ?
                <Nav id='recipients' style={getNavStyle(['RECIPIENTS'])}>
                    <IconCnt>
                        <NavImg dark='/images/nav/nav-icon-dark-Recipients.svg' light='/images/nav/nav-icon-light-Recipients.svg' selected={isActive('RECIPIENTS')} />
                    </IconCnt>
                    <Text>Recipients</Text>
                </Nav>
                :
                null
            }
            <Nav id='billing' style={getNavStyle(['BILLING'])}>
                <IconCnt>
                    <NavImg dark='/images/nav/nav-icon-dark-Billing.svg' light='/images/nav/nav-icon-light-Billing.svg' selected={isActive('BILLING')} />
                </IconCnt>
                <Text>Billing</Text>
            </Nav>

            <Nav id='reports' style={getNavStyle(['REPORTS'])}>
                <IconCnt>
                    <NavImg dark='/images/nav/nav-icon-dark-Reports.svg' light='/images/nav/nav-icon-light-Reports.svg' selected={isActive('REPORTS')} />
                </IconCnt>
                <Text>Reports</Text>
            </Nav>

            <Nav id='regulatory-compliance' style={getNavStyle(['REGULATORY-COMPLIANCE', 'NETWORK-EMAIL-REPOSITORY'])}>
                <IconCnt>
                    <NavImg dark='/images/nav/nav-icon-dark-Regulatory-Compliance.svg' light='/images/nav/nav-icon-light-Regulatory-Compliance.svg' selected={isActive('REGULATORY-COMPLIANCE', 'NETWORK-EMAIL-REPOSITORY')} />
                </IconCnt>
                <Text>Regulatory Compliance</Text>
            </Nav>

            <Nav id='settings' style={getNavStyle(['PROFILE'])}>
                <IconCnt>
                    <NavImg dark='/images/nav/nav-icon-dark-Settings.svg' light='/images/nav/nav-icon-light-Settings.svg' selected={isActive('PROFILE')} />
                </IconCnt>
                <Text>Settings</Text>
            </Nav>
        </SideNav>
    );
}

export default RenderItems2;
